import { useState } from 'react';
import { Link } from 'react-router-dom';
import { runwayMeta, runwayPhotos } from '../data/runway';
import { Icon, Ornament, Reveal } from '../components/Primitives';
import Lightbox from '../components/Lightbox';
import Seo from '../components/Seo';
import { CtaBand, PageHero } from '../components/Shared';

/**
 * One shoot, one grid — no filter rail. The Lookbook needs tabs because it holds
 * three separate stories; this is a single evening, so a filter would be furniture
 * with nothing to sort.
 */
export default function Runway() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <>
      <Seo
        title="On the Ramp"
        description="Nikhil Jewellers on the runway — closed-set polki and kundan bridal suites shown at a bridal couture show in Ahmedabad."
        image="/assets/runway/runway-w-03.webp"
      />

      <PageHero
        eyebrow={runwayMeta.eyebrow}
        title={runwayMeta.title}
        lede={runwayMeta.lede}
        image="/assets/runway/runway-w-03.webp"
        trail={[{ label: 'Home', to: '/' }, { label: runwayMeta.title }]}
      />

      {/* Standfirst */}
      <section className="bg-cream-50 py-16 sm:py-20">
        <div className="shell">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
            <Reveal className="lg:col-span-7">
              <p className="eyebrow">The Show</p>
              <h2 className="display-lg mt-3 text-royal-900">
                Jewellery that has to read
                <br />
                from the back row
              </h2>
              <Ornament className="mt-5 !justify-start" />
              <p className="mt-6 max-w-xl lede">
                A ramp is an unforgiving test. Stage lighting flattens most stones, and a
                piece that photographs beautifully on velvet can disappear at ten paces.
                Closed-set polki holds up because the foil behind each stone throws the
                light back rather than swallowing it.
              </p>
            </Reveal>
            <Reveal delay={110} className="lg:col-span-5 lg:text-right">
              <p className="font-display text-xl font-light italic text-maroon-700/80">
                {runwayMeta.count} frames from the evening
              </p>
              <p className="mt-3 text-[12px] uppercase tracking-wide2 text-royal-700/50">
                {runwayMeta.note}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="bg-cream-100 pb-20 pt-4 sm:pb-28">
        <div className="shell">
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {runwayPhotos.map((photo, i) => (
              <Reveal
                key={photo.src}
                delay={(i % 4) * 70}
                className={photo.wide ? 'col-span-2' : ''}
              >
                <button
                  type="button"
                  onClick={() => setLightbox(i)}
                  aria-label={`Open: ${photo.alt}`}
                  className="group relative block w-full overflow-hidden bg-royal-900
                    ring-1 ring-inset ring-royal-800/10 focus-visible:ring-gold-500"
                >
                  <div
                    className={`overflow-hidden ${photo.wide ? 'aspect-[3/2]' : 'aspect-[2/3]'}`}
                  >
                    <img
                      src={photo.thumb}
                      alt={photo.alt}
                      loading={i < 4 ? 'eager' : 'lazy'}
                      decoding="async"
                      className="h-full w-full object-cover transition-transform
                        duration-[1300ms] ease-luxe group-hover:scale-[1.07]"
                    />
                  </div>
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t
                      from-royal-950/60 to-transparent opacity-0 transition-opacity
                      duration-500 group-hover:opacity-100"
                  />
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute bottom-3 right-3 grid h-9 w-9
                      place-items-center border border-gold-400/50 bg-royal-950/50
                      text-gold-300 opacity-0 transition-all duration-500 ease-luxe
                      group-hover:opacity-100"
                  >
                    <Icon.Arrow className="h-4 w-4 -rotate-45" />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14 text-center">
            <Link to="/lookbook" className="btn-outline">
              See the studio lookbook
              <Icon.Arrow className="h-3.5 w-3.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Lightbox
        photos={runwayPhotos}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onIndex={setLightbox}
      />

      <CtaBand
        eyebrow="On the ramp"
        title="Ask about a piece from the show"
        text="Most of what walked is in the case or can be remade to your measurements. Send us the frame and we will tell you what it is."
      />
    </>
  );
}
